/**
 * Colori centralizzati per categorie approfondimenti — usati in tutto il sito.
 *
 * Categorie reali:
 *   educazione-e-giovani  → arancio
 *   cura-della-vita       → rosa/fucsia
 *   economia-e-societa    → viola
 *   reggio-emilia         → verde smeraldo
 *   uno-sguardo-sul-mondo → indigo (post-fix: prima era sky, troppo simile
 *                             a educazione-e-giovani nei pill a sfondo
 *                             chiaro — il fucsia/arancio chiaro e
 *                             l'azzurro chiaro hanno luminanza simile e
 *                             si confondevano nel menu dropdown)
 *   cultura               → rosa caldo
 *   vita-della-chiesa      → ciano/teal
 */

/* ── Pill / badge nelle card (sfondo chiaro) ──
   BUG-FIX category-colors-lookup (slug reali):
     Gli slug effettivamente persistiti nella tabella categories sono piu'
     corti rispetto ai nomi display:
       - "sguardo-sul-mondo"   (display: "Uno sguardo sul mondo")
       - "educazione"          (display: "Educazione e Giovani")
     Le altre 5 categorie matchano il display. Includo SIA gli slug DB
     reali SIA gli slug "canonici" lunghi (per back-compat con eventuali
     migrazioni future o seed che usano la forma estesa).
   BUG-FIX category-colors-distinct (cultura vs cura-della-vita):
     Pre-fix "cura-della-vita" = pink-100 e "cultura" = rose-100 erano
     visivamente identici (pink e rose in Tailwind sono tinte adiacenti).
     Cambiato "cultura" → amber (oro caldo): nettamente distinto sia
     dal pink di cura-della-vita sia dall'arancio di educazione, e
     semanticamente coerente con il tema "cultural/scholar". */
export const CATEGORY_COLORS: Record<string, string> = {
  "educazione":            "bg-orange-100 text-orange-800",
  "educazione-e-giovani":  "bg-orange-100 text-orange-800",
  "cura-della-vita":       "bg-pink-100 text-pink-800",
  "economia-e-societa":    "bg-violet-100 text-violet-800",
  "reggio-emilia":         "bg-emerald-100 text-emerald-800",
  "sguardo-sul-mondo":     "bg-indigo-100 text-indigo-800",
  "uno-sguardo-sul-mondo": "bg-indigo-100 text-indigo-800",
  "cultura":               "bg-amber-100 text-amber-900",
  "vita-della-chiesa":     "bg-cyan-100 text-cyan-800",
};

/* ── Pill attive nei filtri (sfondo pieno) ── */
export const CATEGORY_ACTIVE_COLORS: Record<string, string> = {
  "educazione":            "bg-orange-600 text-white shadow-md",
  "educazione-e-giovani":  "bg-orange-600 text-white shadow-md",
  "cura-della-vita":       "bg-pink-600 text-white shadow-md",
  "economia-e-societa":    "bg-violet-700 text-white shadow-md",
  "reggio-emilia":         "bg-emerald-700 text-white shadow-md",
  "sguardo-sul-mondo":     "bg-indigo-700 text-white shadow-md",
  "uno-sguardo-sul-mondo": "bg-indigo-700 text-white shadow-md",
  "cultura":               "bg-amber-600 text-white shadow-md",
  "vita-della-chiesa":     "bg-cyan-700 text-white shadow-md",
};

/**
 * BUG-FIX category-colors-lookup:
 *   Normalizza uno slug "raw" arrivato dall'API (admin operator typos,
 *   maiuscole, underscore, accenti italiani, spazi, "&"/"e") nella forma
 *   canonica usata come chiave delle mappe sopra (es. "uno-sguardo-sul-mondo").
 *
 *   Pre-fix la lookup era {@code CATEGORY_COLORS[slug]} secca: se il
 *   backend ritornava {@code "Uno-Sguardo-Sul-Mondo"} (capital U,
 *   inserito a mano in /admin/categorie) il match falliva e il pill
 *   cadeva sul default neutral grigio — visibilmente identico al pill
 *   di altre categorie senza colore custom.
 *
 *   Strategia: lower-case + trim + sostituzioni puntuali per i casi
 *   piu' comuni (separatori, "&" italiano, accenti). Se nemmeno cosi'
 *   matcha, il chiamante riceve la chiave normalizzata: utile per il
 *   debug (basta un Object.keys(CATEGORY_COLORS) per vedere il delta).
 */
function normalizeSlug(slug?: string | null): string | undefined {
  if (!slug) return undefined;
  let s = slug.trim().toLowerCase();
  // separator unification: underscore / spazio / multiple-hyphen
  s = s.replace(/[\s_]+/g, "-").replace(/-+/g, "-");
  // strip leading/trailing hyphens introduced dal replace
  s = s.replace(/^-+|-+$/g, "");
  // accenti italiani comuni (società, città, perché) — la mappa usa
  // varianti senza accento (es. "economia-e-societa")
  s = s
    .replace(/[àáâãä]/g, "a")
    .replace(/[èéêë]/g, "e")
    .replace(/[ìíîï]/g, "i")
    .replace(/[òóôõö]/g, "o")
    .replace(/[ùúûü]/g, "u");
  // "&" / "and" → "e" (it)
  s = s.replace(/&/g, "e").replace(/\band\b/g, "e");
  return s || undefined;
}

/**
 * Lookup tollerante: prova prima la mappa con la chiave normalizzata,
 * poi alcune varianti note (es. "societa" vs "society"). Se nessuna
 * combo matcha, ritorna il default neutral.
 *
 * Esportato cosi' i test possono usarlo direttamente e abbiamo un
 * single source of truth per il matching slug → mappa.
 */
function lookup<T>(map: Record<string, T>, slug: string | undefined, fallback: T): T {
  const key = normalizeSlug(slug);
  if (!key) return fallback;
  if (map[key] !== undefined) return map[key];
  // Varianti "societa" vs "society", "e-giovani" vs "&-giovani", ecc.
  const variants = [
    key.replace(/societa$/, "society"),
    key.replace(/society$/, "societa"),
    key.replace(/-e-/g, "-and-"),
    key.replace(/-and-/g, "-e-"),
  ];
  for (const v of variants) if (map[v] !== undefined) return map[v];
  return fallback;
}

export function categoryColor(slug?: string): string {
  return lookup(CATEGORY_COLORS, slug, "bg-neutral-100 text-neutral-700");
}

export function categoryPillColor(slug?: string, isActive?: boolean): string {
  if (isActive) {
    return lookup(CATEGORY_ACTIVE_COLORS, slug, "bg-neutral-700 text-white shadow-md");
  }
  return categoryColor(slug);
}

/* ── Accent bar per card approfondimenti ──
   BUG-FIX category-colors-lookup (slug reali): vedi commento su
   CATEGORY_COLORS. Inclusi alias "educazione" e "sguardo-sul-mondo"
   per matchare gli slug effettivi del DB. */
export const CATEGORY_ACCENT: Record<string, string> = {
  "educazione":            "from-orange-500 to-orange-300",
  "educazione-e-giovani":  "from-orange-500 to-orange-300",
  "cura-della-vita":       "from-pink-500 to-pink-300",
  "economia-e-societa":    "from-violet-500 to-violet-300",
  "reggio-emilia":         "from-emerald-600 to-emerald-400",
  "sguardo-sul-mondo":     "from-indigo-600 to-indigo-400",
  "uno-sguardo-sul-mondo": "from-indigo-600 to-indigo-400",
  // BUG-FIX category-colors-distinct: cultura passa da rose ad amber
  // per distinguerla da cura-della-vita (entrambi erano pink-adjacent).
  "cultura":               "from-amber-500 to-amber-300",
  "vita-della-chiesa":     "from-cyan-500 to-cyan-300",
};

export function categoryAccent(slug?: string): string {
  return lookup(CATEGORY_ACCENT, slug, "from-[#1a3a5c] to-[#4a90c4]");
}

/* ── Colori completi per HeroCarousel (sfondo slide + post-it) ── */
export interface CatColors {
  bgFrom: string;
  bgTo: string;
  accentRgba: string;
  accent: string;
  badge: string;
  badgeText: string;
  postFrom: string;
  postTo: string;
}

// BUG-FIX category-colors-lookup (slug reali): le definizioni colore
// "ricche" (sfondo + accent + post-it) sono estratte in costanti per
// poter essere riferite da piu' chiavi della mappa (alias DB-slug +
// nome canonico) senza duplicare i literal.
// A11y-FIX badge-contrast: tutti i badge -500 + text-white fallivano
// WCAG AA (4.5:1) per testo 11px bold (es. orange-500 ~2.94:1,
// indigo-500 ~4.34:1, amber-500 ~1.94:1). Bumpati a -700 per garantire
// >=5.3:1 su white (Lighthouse a11y: "Il rapporto di contrasto tra i
// colori di sfondo e primo piano non è sufficiente" sulla card
// "UNO SGUARDO SUL MONDO").
const COL_EDUCAZIONE: CatColors = {
  bgFrom: "#1c0800", bgTo: "#3b1500",
  accentRgba: "rgba(234,88,12,0.35)", accent: "#ea580c",
  badge: "bg-orange-700", badgeText: "text-white",
  postFrom: "#ffedd5", postTo: "#fdba74",
};
const COL_UNO_SGUARDO: CatColors = {
  // BUG-FIX category-colors: passato da sky a indigo. Indigo e' piu'
  // saturo del sky chiaro e si distingue nettamente dal pill arancio
  // di educazione anche nei dot del menu dropdown (dove la dimensione
  // del cerchietto e' molto piccola).
  bgFrom: "#0a0a23", bgTo: "#1a1a4a",
  accentRgba: "rgba(99,102,241,0.35)", accent: "#6366f1",
  badge: "bg-indigo-700", badgeText: "text-white",
  postFrom: "#e0e7ff", postTo: "#a5b4fc",
};

export const CAT_COLORS: Record<string, CatColors> = {
  // slug reale DB
  "educazione":            COL_EDUCAZIONE,
  // alias canonico (back-compat)
  "educazione-e-giovani":  COL_EDUCAZIONE,
  "cura-della-vita": {
    bgFrom: "#1a0008", bgTo: "#3d0018",
    accentRgba: "rgba(236,72,153,0.35)", accent: "#ec4899",
    // A11y-FIX: pink-500+white ~3.05:1 → pink-700+white ~5.96:1
    badge: "bg-pink-700", badgeText: "text-white",
    postFrom: "#fce7f3", postTo: "#f9a8d4",
  },
  "economia-e-societa": {
    bgFrom: "#0f0524", bgTo: "#200a48",
    accentRgba: "rgba(167,139,250,0.35)", accent: "#a78bfa",
    // A11y-FIX: violet-500+white ~3.66:1 → violet-700+white ~7.41:1
    badge: "bg-violet-700", badgeText: "text-white",
    postFrom: "#f3e8ff", postTo: "#c4b5fd",
  },
  "reggio-emilia": {
    bgFrom: "#000e09", bgTo: "#00271a",
    accentRgba: "rgba(52,211,153,0.35)", accent: "#34d399",
    // A11y-FIX: emerald-500+white ~2.42:1 → emerald-700+white ~5.31:1
    badge: "bg-emerald-700", badgeText: "text-white",
    postFrom: "#dcfce7", postTo: "#86efac",
  },
  // slug reale DB
  "sguardo-sul-mondo":     COL_UNO_SGUARDO,
  // alias canonico (back-compat)
  "uno-sguardo-sul-mondo": COL_UNO_SGUARDO,
  "cultura": {
    // BUG-FIX category-colors-distinct: passato da rose ad amber.
    // Pre-fix il post-it della cultura era rosa salmone, identico a
    // "cura-della-vita" che e' pink. Ora oro caldo (amber) — distinto
    // sia dal pink che dall'arancio di educazione, e semanticamente
    // coerente con tema scholar/culturale.
    bgFrom: "#1f1200", bgTo: "#3d2400",
    accentRgba: "rgba(245,158,11,0.35)", accent: "#f59e0b",
    // A11y-FIX: amber-500+white ~1.94:1 (gravissimo) → amber-800+white ~6.6:1
    badge: "bg-amber-800", badgeText: "text-white",
    postFrom: "#fef3c7", postTo: "#fcd34d",
  },
  "vita-della-chiesa": {
    bgFrom: "#000d14", bgTo: "#00202f",
    accentRgba: "rgba(34,211,238,0.35)", accent: "#22d3ee",
    // A11y-FIX: cyan-500+white ~2.27:1 → cyan-800+white ~7.05:1
    badge: "bg-cyan-800", badgeText: "text-white",
    postFrom: "#cffafe", postTo: "#67e8f9",
  },
};

export const DEFAULT_CAT_COLORS: CatColors = {
  bgFrom: "#06101c", bgTo: "#0f1f33",
  accentRgba: "rgba(74,144,196,0.3)", accent: "#4a90c4",
  badge: "bg-[#4a90c4]", badgeText: "text-white",
  postFrom: "#fef9c3", postTo: "#fde047",
};

export function getCatColors(slug?: string): CatColors {
  return lookup(CAT_COLORS, slug, DEFAULT_CAT_COLORS);
}

/**
 * BUG-FIX category-colors-lookup (helper di debug):
 *   Esposta la normalizzazione per gli unit test e per consentire al
 *   chiamante di loggare lo slug normalizzato accanto al pill quando
 *   la mappa non matcha (DevTools mostra il fallback neutral grigio).
 */
export const __internal = { normalizeSlug };
